import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  Sequence,
} from 'remotion';

// ==================== 配置 ====================
const COLORS = {
  background: '#0a0a0a',
  primary: '#ff6b35', // 暖橙
  secondary: '#4ecdc4', // 冷青
  accent: '#ffe66d', // 柔黄
  text: '#ffffff',
  textMuted: '#888888',
};

const GRADIENTS = {
  warm: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 50%, #ffe66d 100%)',
  cool: 'linear-gradient(135deg, #4ecdc4 0%, #44a3aa 100%)',
  xiaohongshu: 'linear-gradient(135deg, #ff6b9d 0%, #ff8e53 50%, #ffe66d 100%)',
};

// ==================== 公共组件 ====================

// 背景组件
const XHSBackground: React.FC<{ gradient?: string }> = ({ gradient = GRADIENTS.xiaohongshu }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const opacity = spring({
    frame,
    fps,
    config: { damping: 100 },
  });
  
  return (
    <AbsoluteFill>
      {/* 基础背景 */}
      <AbsoluteFill style={{ backgroundColor: COLORS.background }} />
      
      {/* 渐变覆盖层（降低饱和度 30%）*/}
      <AbsoluteFill
        style={{
          background: gradient,
          opacity: opacity * 0.15,
          filter: 'saturate(0.7)',
        }}
      />
    </AbsoluteFill>
  );
};

// 文字组件
const XHSText: React.FC<{
  children: React.ReactNode;
  style?: React.CSSProperties;
  delay?: number;
}> = ({ children, style, delay = 0 }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const progress = spring({
    frame: frame - delay,
    fps,
    config: { damping: 12, stiffness: 100 },
  });
  
  const translateY = interpolate(progress, [0, 1], [50, 0]);
  const opacity = Math.max(0, Math.min(1, progress));
  
  return (
    <div
      style={{
        color: COLORS.text,
        transform: `translateY(${translateY}px)`,
        opacity,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

// 占位图片组件
const PlaceholderImage: React.FC<{
  width: number;
  height: number;
  color?: string;
  label?: string;
}> = ({ width, height, color = COLORS.primary, label }) => {
  return (
    <div
      style={{
        width,
        height,
        background: `linear-gradient(135deg, ${color}40 0%, ${color}20 100%)`,
        borderRadius: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `2px solid ${color}60`,
      }}
    >
      {label && (
        <span style={{ color: COLORS.textMuted, fontSize: 40 }}>{label}</span>
      )}
    </div>
  );
};

// CTA 按钮
const CTAButton: React.FC<{ text: string; delay?: number }> = ({ text, delay = 0 }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const scale = spring({
    frame: frame - delay,
    fps,
    config: { damping: 8, stiffness: 200 },
  });
  
  const pulse = Math.sin(frame * 0.1) * 0.05 + 1;
  
  return (
    <div
      style={{
        background: GRADIENTS.warm,
        padding: '24px 64px',
        borderRadius: 100,
        fontSize: 56,
        fontWeight: 'bold',
        color: COLORS.background,
        transform: `scale(${scale * pulse})`,
        boxShadow: '0 8px 32px rgba(255, 107, 53, 0.4)',
      }}
    >
      {text}
    </div>
  );
};

// ==================== 1️⃣ 产品对比模板（15秒）====================

const ProductComparisonHook: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const titleScale = spring({
    frame,
    fps,
    config: { damping: 12, stiffness: 100 },
  });
  
  const subtitleOpacity = spring({
    frame: frame - 15,
    fps,
    config: { damping: 100 },
  });
  
  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center', gap: 40 }}>
      <XHSText
        style={{
          fontSize: 120,
          fontWeight: 'bold',
          textAlign: 'center',
          transform: `scale(${titleScale})`,
        }}
      >
        到底买哪个？
      </XHSText>
      
      <XHSText
        delay={15}
        style={{
          fontSize: 60,
          color: COLORS.textMuted,
          opacity: subtitleOpacity,
        }}
      >
        产品 A vs 产品 B
      </XHSText>
    </AbsoluteFill>
  );
};

const ProductComparisonMain: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const dimensions = [
    { label: '价格', productA: '599元', productB: '399元', emoji: '💰' },
    { label: '性能', productA: '⭐⭐⭐⭐⭐', productB: '⭐⭐⭐⭐', emoji: '⚡' },
    { label: '续航', productA: '12小时', productB: '8小时', emoji: '🔋' },
    { label: '拍照', productA: '4800万', productB: '3200万', emoji: '📸' },
    { label: '系统', productA: '流畅', productB: '一般', emoji: '📱' },
  ];
  
  return (
    <AbsoluteFill style={{ padding: 80, paddingTop: 160 }}>
      {/* 标题 */}
      <XHSText
        style={{
          fontSize: 64,
          fontWeight: 'bold',
          marginBottom: 60,
        }}
      >
        5 维度对比
      </XHSText>
      
      {/* 对比卡片 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
        {dimensions.map((dim, i) => {
          const delay = i * 8;
          const progress = spring({
            frame: frame - delay,
            fps,
            config: { damping: 12 },
          });
          
          const translateX = interpolate(progress, [0, 1], [500, 0]);
          
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 30,
                padding: '24px 32px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 20,
                transform: `translateX(${translateX}px)`,
                opacity: progress,
              }}
            >
              <span style={{ fontSize: 48 }}>{dim.emoji}</span>
              <span style={{ fontSize: 48, fontWeight: 'bold', width: 120 }}>
                {dim.label}
              </span>
              <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 42, color: COLORS.primary }}>{dim.productA}</span>
                <span style={{ fontSize: 42, color: COLORS.textMuted }}>{dim.productB}</span>
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

const ProductComparisonCTA: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const progress = spring({
    frame,
    fps,
    config: { damping: 12 },
  });
  
  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center', gap: 60 }}>
      <XHSText
        style={{
          fontSize: 80,
          fontWeight: 'bold',
          textAlign: 'center',
          transform: `scale(${progress})`,
        }}
      >
        推荐：产品 A ✅
      </XHSText>
      
      <XHSText
        delay={10}
        style={{
          fontSize: 48,
          color: COLORS.textMuted,
          textAlign: 'center',
        }}
      >
        性价比更高，性能更强
      </XHSText>
      
      <CTAButton text="点赞关注" delay={20} />
    </AbsoluteFill>
  );
};

export const ProductComparisonTemplate: React.FC = () => {
  const { fps } = useVideoConfig();
  
  return (
    <AbsoluteFill>
      <XHSBackground />
      
      {/* 开场（3秒 = 90帧 @30fps）*/}
      <Sequence from={0} durationInFrames={fps * 3}>
        <ProductComparisonHook />
      </Sequence>
      
      {/* 主体（10秒 = 300帧 @30fps）*/}
      <Sequence from={fps * 3} durationInFrames={fps * 10}>
        <ProductComparisonMain />
      </Sequence>
      
      {/* 结尾（2秒 = 60帧 @30fps）*/}
      <Sequence from={fps * 13} durationInFrames={fps * 2}>
        <ProductComparisonCTA />
      </Sequence>
    </AbsoluteFill>
  );
};

// ==================== 2️⃣ 美食探店模板（20秒）====================

const FoodReviewHook: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const scale = spring({
    frame,
    fps,
    config: { damping: 10, stiffness: 100 },
  });
  
  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center', gap: 40 }}>
      <XHSText
        style={{
          fontSize: 100,
          fontWeight: 'bold',
          textAlign: 'center',
          transform: `scale(${scale})`,
        }}
      >
        这家店值得去吗？
      </XHSText>
      
      <XHSText
        delay={20}
        style={{
          fontSize: 72,
          color: COLORS.primary,
          fontWeight: 'bold',
        }}
      >
        🍴 店名：XX 餐厅
      </XHSText>
      
      <PlaceholderImage
        width={600}
        height={400}
        label="店铺照片"
        color={COLORS.primary}
      />
    </AbsoluteFill>
  );
};

const FoodReviewMain: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const dishes = [
    {
      name: '招牌菜 A',
      taste: '⭐⭐⭐⭐⭐',
      comment: '口感鲜美，分量十足',
      price: '88元',
    },
    {
      name: '推荐菜 B',
      taste: '⭐⭐⭐⭐',
      comment: '味道不错，值得尝试',
      price: '68元',
    },
    {
      name: '必点菜 C',
      taste: '⭐⭐⭐⭐⭐',
      comment: '性价比超高，强烈推荐',
      price: '48元',
    },
  ];
  
  return (
    <AbsoluteFill style={{ padding: 60, paddingTop: 120 }}>
      <XHSText
        style={{
          fontSize: 64,
          fontWeight: 'bold',
          marginBottom: 40,
        }}
      >
        3 道菜实测 🍽️
      </XHSText>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
        {dishes.map((dish, i) => {
          const delay = i * 25;
          const progress = spring({
            frame: frame - delay,
            fps,
            config: { damping: 12 },
          });
          
          const translateY = interpolate(progress, [0, 1], [100, 0]);
          
          return (
            <div
              key={i}
              style={{
                padding: '32px 40px',
                background: 'rgba(255, 255, 255, 0.08)',
                borderRadius: 24,
                transform: `translateY(${translateY}px)`,
                opacity: progress,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <span style={{ fontSize: 52, fontWeight: 'bold' }}>{dish.name}</span>
                <span style={{ fontSize: 48 }}>{dish.price}</span>
              </div>
              
              <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                <span style={{ fontSize: 40 }}>{dish.taste}</span>
                <span style={{ fontSize: 40, color: COLORS.textMuted }}>{dish.comment}</span>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* 环境提示 */}
      <XHSText
        delay={80}
        style={{
          fontSize: 40,
          color: COLORS.textMuted,
          marginTop: 30,
          textAlign: 'center',
        }}
      >
        🏠 环境舒适 | 💰 人均 68 元
      </XHSText>
    </AbsoluteFill>
  );
};

const FoodReviewCTA: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const scale = spring({
    frame,
    fps,
    config: { damping: 10 },
  });
  
  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center', gap: 50 }}>
      <XHSText
        style={{
          fontSize: 90,
          fontWeight: 'bold',
          textAlign: 'center',
          transform: `scale(${scale})`,
        }}
      >
        综合评分
      </XHSText>
      
      <div
        style={{
          fontSize: 120,
          fontWeight: 'bold',
          color: COLORS.primary,
          transform: `scale(${scale})`,
        }}
      >
        ⭐⭐⭐⭐⭐ 4.8/5
      </div>
      
      <XHSText
        delay={15}
        style={{
          fontSize: 56,
          fontWeight: 'bold',
          color: COLORS.secondary,
        }}
      >
        推荐指数：⭐⭐⭐⭐⭐
      </XHSText>
      
      <CTAButton text="收藏打卡" delay={30} />
    </AbsoluteFill>
  );
};

export const FoodReviewTemplate: React.FC = () => {
  const { fps } = useVideoConfig();
  
  return (
    <AbsoluteFill>
      <XHSBackground gradient={GRADIENTS.warm} />
      
      {/* 开场（3秒）*/}
      <Sequence from={0} durationInFrames={fps * 3}>
        <FoodReviewHook />
      </Sequence>
      
      {/* 主体（14秒）*/}
      <Sequence from={fps * 3} durationInFrames={fps * 14}>
        <FoodReviewMain />
      </Sequence>
      
      {/* 结尾（3秒）*/}
      <Sequence from={fps * 17} durationInFrames={fps * 3}>
        <FoodReviewCTA />
      </Sequence>
    </AbsoluteFill>
  );
};

// ==================== 3️⃣ 好物分享模板（15秒）====================

const PRODUCT_COLORS = {
  teal: '#14B8A6',
  cyan: '#06B6D4',
  emerald: '#10B981',
  violet: '#8B5CF6',
  surface: 'rgba(255, 255, 255, 0.06)',
};

const ProductFloatingBackground: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  const shapes = Array.from({ length: 18 }, (_, i) => {
    const x = (i * 63.7) % width;
    const baseY = (i * 107.3) % height;
    const floatY = Math.sin(frame * 0.015 + i * 0.8) * 30;
    const floatX = Math.cos(frame * 0.01 + i * 0.6) * 15;
    const size = 60 + (i % 5) * 40;
    const opacity = 0.04 + Math.sin(frame * 0.012 + i * 0.4) * 0.02;
    const rotate = frame * 0.15 + i * 30;
    const colors = [PRODUCT_COLORS.teal, PRODUCT_COLORS.cyan, PRODUCT_COLORS.emerald, PRODUCT_COLORS.violet];
    return { x: x + floatX, y: baseY + floatY, size, opacity, rotate, color: colors[i % colors.length] };
  });

  return (
    <AbsoluteFill style={{ overflow: 'hidden' }}>
      <AbsoluteFill style={{ background: 'linear-gradient(180deg, #020617 0%, #0F172A 50%, #1E293B 100%)' }} />

      <div
        style={{
          position: 'absolute',
          top: -150,
          right: -100,
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${PRODUCT_COLORS.teal}12 0%, transparent 70%)`,
          filter: 'blur(50px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: -150,
          left: -100,
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${PRODUCT_COLORS.cyan}10 0%, transparent 70%)`,
          filter: 'blur(50px)',
        }}
      />

      {shapes.map((s, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: s.x,
            top: s.y,
            width: s.size,
            height: s.size,
            border: `1.5px solid ${s.color}`,
            borderRadius: i % 3 === 0 ? '50%' : i % 3 === 1 ? 8 : 0,
            opacity: s.opacity,
            transform: `rotate(${s.rotate}deg)`,
          }}
        />
      ))}
    </AbsoluteFill>
  );
};

const ProductReviewHook: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleProgress = spring({
    frame,
    fps,
    config: { damping: 14, stiffness: 120 },
  });

  const subtitleProgress = spring({
    frame: frame - 18,
    fps,
    config: { damping: 16, stiffness: 100 },
  });

  const scale = interpolate(titleProgress, [0, 1], [0.5, 1]);
  const lineWidth = interpolate(titleProgress, [0, 1], [0, 280]);

  const tagProgress = spring({
    frame: frame - 30,
    fps,
    config: { damping: 12, stiffness: 150 },
  });

  return (
    <AbsoluteFill
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 32,
      }}
    >
      {/* 标签 */}
      <div
        style={{
          background: `linear-gradient(135deg, ${PRODUCT_COLORS.teal} 0%, ${PRODUCT_COLORS.cyan} 100%)`,
          padding: '10px 32px',
          borderRadius: 40,
          fontSize: 45,
          fontWeight: 700,
          color: '#FFFFFF',
          opacity: tagProgress,
          transform: `scale(${interpolate(tagProgress, [0, 1], [0.6, 1])})`,
          letterSpacing: 2,
        }}
      >
        真实测评
      </div>

      {/* 主标题 */}
      <div
        style={{
          fontSize: 88,
          fontWeight: 900,
          color: '#FFFFFF',
          textAlign: 'center',
          transform: `scale(${scale})`,
          opacity: titleProgress,
          lineHeight: 1.2,
          letterSpacing: 1,
        }}
      >
        我用 3 个月后…
      </div>

      {/* 分隔线 */}
      <div
        style={{
          width: lineWidth,
          height: 3,
          background: `linear-gradient(90deg, ${PRODUCT_COLORS.teal}, ${PRODUCT_COLORS.cyan})`,
          borderRadius: 2,
        }}
      />

      {/* 副标题 */}
      <div
        style={{
          fontSize: 50,
          fontWeight: 600,
          color: 'rgba(255,255,255,0.65)',
          textAlign: 'center',
          opacity: subtitleProgress,
          transform: `translateY(${interpolate(subtitleProgress, [0, 1], [20, 0])}px)`,
        }}
      >
        解决了我的痛点 💡
      </div>

      <PlaceholderImage
        width={480}
        height={480}
        label="产品图"
        color={PRODUCT_COLORS.teal}
      />
    </AbsoluteFill>
  );
};

const ProductReviewMain: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const highlights = [
    {
      title: '功能强大',
      description: '满足所有使用场景，日常工作效率提升 40%',
      emoji: '⚡',
      color: PRODUCT_COLORS.teal,
      delay: 0,
    },
    {
      title: '体验舒适',
      description: '人性化设计，上手零门槛，老人小孩都能用',
      emoji: '👍',
      color: PRODUCT_COLORS.cyan,
      delay: 30,
    },
    {
      title: '性价比超高',
      description: '同级别产品中价格最低，品质不输大牌',
      emoji: '💰',
      color: PRODUCT_COLORS.emerald,
      delay: 60,
    },
  ];

  const titleProgress = spring({
    frame,
    fps,
    config: { damping: 14, stiffness: 140 },
  });

  return (
    <AbsoluteFill style={{ padding: 48, paddingTop: 80 }}>
      <div
        style={{
          fontSize: 60,
          fontWeight: 800,
          color: '#FFFFFF',
          marginBottom: 36,
          opacity: titleProgress,
          transform: `translateY(${interpolate(titleProgress, [0, 1], [-20, 0])}px)`,
          letterSpacing: 1,
        }}
      >
        3 大亮点 ✨
      </div>
      <div
        style={{
          width: interpolate(titleProgress, [0, 1], [0, 140]),
          height: 3,
          background: `linear-gradient(90deg, ${PRODUCT_COLORS.teal}, ${PRODUCT_COLORS.cyan})`,
          borderRadius: 2,
          marginBottom: 80,
        }}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
        {highlights.map((item, i) => {
          const progress = spring({
            frame: frame - item.delay,
            fps,
            config: { damping: 14, stiffness: 100 },
          });

          const translateX = interpolate(progress, [0, 1], [500, 0]);
          const cardScale = interpolate(progress, [0, 1], [0.88, 1]);
          const shimmer = 0.06 + Math.sin(frame * 0.05 + i * 1.5) * 0.03;

          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 28,
                padding: '36px 40px',
                background: `linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)`,
                backdropFilter: 'blur(16px)',
                borderRadius: 24,
                borderLeft: `5px solid ${item.color}`,
                border: `1.5px solid ${item.color}30`,
                borderLeftWidth: 5,
                borderLeftColor: item.color,
                transform: `translateX(${translateX}px) scale(${cardScale})`,
                opacity: progress,
                boxShadow: `0 4px 24px ${item.color}15, inset 0 0 40px rgba(255,255,255,${shimmer})`,
              }}
            >
              <div
                style={{
                  fontSize: 64,
                  width: 80,
                  height: 80,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `${item.color}15`,
                  borderRadius: 20,
                }}
              >
                {item.emoji}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 46,
                    fontWeight: 700,
                    color: '#FFFFFF',
                    marginBottom: 10,
                  }}
                >
                  {item.title}
                </div>
                <div
                  style={{
                    fontSize: 40,
                    color: 'rgba(255,255,255,0.55)',
                    lineHeight: 1.4,
                  }}
                >
                  {item.description}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

const ProductReviewCTA: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const progress = spring({
    frame,
    fps,
    config: { damping: 12, stiffness: 140 },
  });

  const scale = interpolate(progress, [0, 1], [0.4, 1]);
  const pulse = 1 + Math.sin(frame * 0.2) * 0.03;

  const starsProgress = spring({
    frame: frame - 15,
    fps,
    config: { damping: 14, stiffness: 120 },
  });

  const promoProgress = spring({
    frame: frame - 25,
    fps,
    config: { damping: 16, stiffness: 100 },
  });

  return (
    <AbsoluteFill
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 36,
      }}
    >
      {/* 标题 */}
      <div
        style={{
          fontSize: 72,
          fontWeight: 900,
          color: '#FFFFFF',
          textAlign: 'center',
          transform: `scale(${scale})`,
          opacity: progress,
          letterSpacing: 2,
        }}
      >
        购买建议
      </div>

      {/* 评分 */}
      <div
        style={{
          fontSize: 64,
          fontWeight: 800,
          background: `linear-gradient(135deg, ${PRODUCT_COLORS.teal} 0%, ${PRODUCT_COLORS.cyan} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textAlign: 'center',
          opacity: starsProgress,
          transform: `scale(${interpolate(starsProgress, [0, 1], [0.7, 1]) * pulse})`,
          marginBottom: 16,
        }}
      >
        强烈推荐 ⭐⭐⭐⭐⭐
      </div>

      {/* 优惠信息 */}
      <div
        style={{
          background: 'rgba(255,255,255,0.06)',
          padding: '16px 40px',
          borderRadius: 16,
          border: `1.5px solid ${PRODUCT_COLORS.teal}30`,
          fontSize: 40,
          color: 'rgba(255,255,255,0.6)',
          textAlign: 'center',
          opacity: promoProgress,
          transform: `translateY(${interpolate(promoProgress, [0, 1], [16, 0])}px)`,
          marginBottom: 25,
        }}
      >
        限时优惠：领券立减 50 元
      </div>

      <CTAButton text="立即购买" delay={30} />
    </AbsoluteFill>
  );
};

export const ProductReviewTemplate: React.FC = () => {
  const { fps } = useVideoConfig();

  return (
    <AbsoluteFill>
      <ProductFloatingBackground />

      <Sequence from={0} durationInFrames={fps * 3}>
        <ProductReviewHook />
      </Sequence>

      <Sequence from={fps * 3} durationInFrames={fps * 10}>
        <ProductReviewMain />
      </Sequence>

      <Sequence from={fps * 13} durationInFrames={fps * 2}>
        <ProductReviewCTA />
      </Sequence>
    </AbsoluteFill>
  );
};
